<template>
  <footer
    class="footer-wrapper"
    :style="{
      backgroundColor: footerGlobal.config.general.backgroundColor.value,
    }"
  >
    <div class="footer-container">
      <div class="footer-top">
        <div class="footer-header">
          <a
            class="logo-wrapper"
            :href="jobShopUrl ? jobShopUrl : null"
            target="_self"
          >
            <img
              v-if="
                brandingGlobal.config.general.logo.media.mediaType === 'image'
              "
              class="logo"
              :src="brandingGlobal.config.general.logo.media.url"
              alt="Logo with link back to the homepage"
              data-testid="footer-logo"
              loading="lazy"
            />
            <IconCompanyLogo v-else fill="#2C389C" />
          </a>

          <div
            class="social-icons"
            data-testid="footer-social-icons"
            :style="{
              color: footerGlobal.config.general.socialIconsColor.value,
            }"
          >
            <template
              v-for="socialChannel in socialMediaGlobal.config.general
                .socialChannels"
            >
              <a
                v-if="socialChannel.url"
                :key="socialChannel.label"
                target="_blank"
                :href="socialChannel.url"
                :title="socialChannel.label"
                :data-testid="`footer-social-icon-${socialChannel.label}`"
              >
                <component :is="getComponentName(socialChannel)" />
              </a>
            </template>
          </div>
        </div>

        <div class="footer-links">
          <ul v-if="footerNavLinks.length" class="links-list">
            <li v-for="link in footerNavLinks" :key="link.label">
              <a
                :style="footerNavLinkElsStyles"
                :href="link.url"
                data-testid="footer-navigation-link"
                >{{ link.label }}</a
              >
            </li>
          </ul>
        </div>
      </div>

      <div class="footer-bottom">
        <div class="footer-bottom-links-container">
          <div class="upper-section" data-testid="footer-secondary-links">
            <a
              :style="footerNavLinkElsStyles"
              target="_blank"
              :href="secondaryLinks.imprint.url"
              data-testid="footer-imprint"
            >
              {{ secondaryLinks.imprint.label }}
            </a>
            <a
              :style="footerNavLinkElsStyles"
              target="_blank"
              :href="secondaryLinks.privacy.url"
              data-testid="footer-privacy"
            >
              {{ secondaryLinks.privacy.label }}
            </a>
          </div>
          <div class="lower-section">
            <button
              class="button-cookie-settings"
              :style="footerNavLinkElsStyles"
              data-testid="footer-cookie-settings"
              @click="$emit('cookieSettingsClick')"
            >
              {{ secondaryLinks.cookieSettings.label }}
            </button>
          </div>
        </div>

        <div
          v-if="footerGlobal.config.general.hintText"
          :style="footerNavLinkElsStyles"
          class="hint-text"
          data-testid="footer-hint-text"
        >
          {{ footerGlobal.config.general.hintText }}
        </div>
      </div>
    </div>
  </footer>
</template>

<script>
import footerGlobal from "./footerDefaultGlobalProp.js";
import socialMediaGlobal from "../../globalSettingProps/socialMediaDefaultGlobalProp";
import brandingGlobal from "../../globalSettingProps/brandingDefaultGlobalProp";
import navigationGlobal from "../Navigation/navigationDefaultGlobalProps.js";

import IconCompanyLogo from "../Icons/IconCompanyLogo.vue";
import IconFacebook from "../Icons/Social/IconFacebook.vue";
import IconX from "../Icons/Social/IconX.vue";
import IconInstagram from "../Icons/Social/IconInstagram.vue";
import IconLinkedin from "../Icons/Social/IconLinkedin.vue";
import IconXing from "../Icons/Social/IconXing.vue";
import IconYoutube from "../Icons/Social/IconYoutube.vue";

export default {
  name: "Footer",
  components: {
    IconCompanyLogo,
    IconFacebook,
    IconX,
    IconInstagram,
    IconLinkedin,
    IconXing,
    IconYoutube,
  },
  props: {
    footerGlobal: {
      type: Object,
      default() {
        return footerGlobal;
      },
    },
    socialMediaGlobal: {
      type: Object,
      default() {
        return socialMediaGlobal;
      },
    },
    brandingGlobal: {
      type: Object,
      default() {
        return brandingGlobal;
      },
    },
    navigationGlobal: {
      type: Object,
      default() {
        return navigationGlobal;
      },
    },
    jobShopUrl: {
      type: String,
      default: "",
    },
  },
  emits: ["cookieSettingsClick"],
  computed: {
    /**
     * Flattens the list of links for the footer omitting group names
     */
    footerNavLinks() {
      return this.navigationGlobal.config.general.links.flatMap((link) => {
        if (link.links) {
          return link.links;
        }
        return link;
      });
    },
    secondaryLinks() {
      return this.footerGlobal.config.general.secondaryLinks;
    },
    footerNavLinkElsStyles() {
      const { textColor } = this.footerGlobal.config.general;

      // direct styles
      const styles = {
        color: textColor.value,
      };
      return styles;
    },
  },
  methods: {
    getComponentName(socialChannel) {
      return `Icon${socialChannel.label[0].toUpperCase()}${socialChannel.label.slice(
        1,
      )}`;
    },
  },
};
</script>

<style scoped lang="scss">
@include reset;

.footer-wrapper {
  @include font-family("body");

  container-type: inline-size;
  contain: layout;
  display: flex;
  flex-direction: column;
  padding: 0 32px;

  a {
    text-decoration: none;
  }
}

.footer-container {
  display: flex;
  flex-direction: column;
  gap: 80px;

  padding: 80px 128px;

  font-size: var(--cr-navigation-xl-size-size);
  font-weight: var(--cr-navigation-xl-weight);
  line-height: var(--cr-navigation-xl-line-height);
  letter-spacing: var(--cr-navigation-xl-letter-spacing);

  @include tablet {
    padding-inline: 64px;
  }

  @include mobile {
    padding-inline: 0;
  }

  .footer-top {
    display: flex;
    justify-content: space-between;
    gap: 32px;

    @include tablet {
      flex-direction: column;
      gap: 64px;
    }
  }
}

.logo {
  display: flex;
  max-width: 250px;
  max-height: 64px;
  object-fit: contain;

  @include tablet {
    max-width: 200px;
  }

  @include mobile {
    max-width: 150px;
    max-height: 36px;
  }
}

svg#icon-company-logo {
  width: 64px;
  height: 64px;

  @include tablet {
    width: 36px;
    height: 36px;
  }
}

svg#icon-youtube {
  width: 30px;

  @include mobile {
    width: 28px;
  }
}

.footer-header {
  display: flex;
  flex-direction: column;
  gap: 128px;
  justify-content: space-between;

  @include tablet {
    flex-direction: row;
    gap: 0;
    align-items: center;
  }

  @include mobile {
    align-items: flex-start;
  }

  .social-icons {
    display: flex;
    gap: 32px;

    @include mobile {
      flex-wrap: wrap;
      gap: 25px 33px;
      max-width: 145px;
    }

    a {
      &:visited,
      &:link,
      &:focus,
      &:hover {
        cursor: pointer;
        color: currentcolor;
      }
    }

    svg {
      width: 25px;
      height: 25px;
    }
  }
}

.footer-links {
  @include tablet {
    display: flex;
    align-items: flex-start;
    margin-left: 0;
  }

  @include mobile {
    display: flex;
    flex-direction: column;
    gap: 25px;
    align-items: center;
  }

  .links-list {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 32px;

    column-fill: auto;
    list-style-type: none;
    margin-bottom: 16px;

    a {
      font-size: var(--cr-navigation-xl-size);
      font-weight: var(--cr-navigation-xl-weight);
      font-style: normal;
      line-height: var(--cr-navigation-xl-line-height);
      letter-spacing: var(--cr-navigation-xl-letter-spacing);
    }

    @include tablet {
      text-align: center;
      grid-template-columns: repeat(2, 1fr);
      width: 100%;
    }

    @include mobile {
      display: flex;
      flex-direction: column;
      gap: 25px;
      align-items: center;
      margin-bottom: 0;
    }
  }
}

.footer-bottom {
  a,
  button,
  .hint-text {
    font-size: 13px;
    line-height: 17px;
    color: #86868b;
    cursor: pointer;
  }

  .hint-text {
    text-align: center;
  }
}

.footer-bottom-links-container {
  display: flex;
  flex-wrap: wrap;
  gap: 64px;
  justify-content: center;

  padding: 42px 0;

  text-align: center;

  border-top: 1px solid rgb(0 0 0 / 8%);

  @include mobile {
    flex-direction: column;
  }

  .upper-section,
  .lower-section {
    display: flex;
    gap: 64px;
    align-items: center;
    justify-content: space-between;
  }

  .upper-section {
    @include mobile {
      width: 250px;
      margin: 0 auto;
    }
  }

  .lower-section {
    cursor: pointer;

    @include mobile {
      display: flex;
      justify-content: center;

      min-width: 250px;
      margin: 0 auto;

      text-align: center;
    }
  }
}

.button-cookie-settings {
  @include reset-button;
}
</style>
